import { uniqWith } from 'es-toolkit';

import { allPairs, notNullish, pairwise, sortBy } from '@paragon/utilities';

import LineSegment2D from './LineSegment2D';
import Point2D from './Point2D';
import Point3D from './Point3D';
import Polygon2D from './Polygon2D';
import Polygon3D, { getEmbedding, tryGetNormalDirection } from './Polygon3D';
import { angleEquals, angleLessThanOrEqual, fullCircle, straightAngle } from './units';

/**
 * This is the client-side version of `PolygonValidationUtilities.AreConnectedSegmentsSelfIntersecting`
 */
export function areConnectedSegmentsSelfIntersecting(segments: readonly LineSegment2D[]) {
  if (connectedSegmentsFormConvexPolygon(segments)) {
    return false;
  }

  const n = segments.length;

  // non adjacent segments should not intersect
  for (let i = 0; i < n; i++) {
    const segment1 = segments[i];
    for (let j = 2; j < n / 2 + 1; j++) {
      const segment2 = segments[(i + j) % n];
      if (!segment1.intersectWithSegment(segment2)) continue;

      // Sometimes if there's a short segment with one end at an acute angle, the two segments neighboring
      // the short one will be close enough to be considered intersecting in the test above, but the
      // polygon is actually valid.
      // Illustration:
      //     /|_____
      //    /       |
      //   /        |
      //  /_________|

      if (j !== 2) return true;

      // The segments adjacent to the short one are expected to lead away from each other.
      const segment1Line = segment1.toLine();
      const segment2Line = segment2.toLine();
      if (
        segment1Line.isParallelTo(segment2Line) ||
        segment1Line.containsPoint(segment2.endPoint) ||
        segment2Line.containsPoint(segment1.basePoint)
      ) {
        return true;
      }

      // Verify whether the segments adjacent to the short one actually intersect each other.

      const rotatedSegment2Line = segment2Line.rotateQuarterTurnAboutBasePoint();
      const rotatedSegment1Line = segment1Line.rotateQuarterTurnAboutBasePoint();

      if (
        Math.sign(segment1.basePoint.projectedSignedDistanceAlong(rotatedSegment2Line)) !==
          Math.sign(segment1.endPoint.projectedSignedDistanceAlong(rotatedSegment2Line)) &&
        Math.sign(segment2.basePoint.projectedSignedDistanceAlong(rotatedSegment1Line)) !==
          Math.sign(segment2.endPoint.projectedSignedDistanceAlong(rotatedSegment1Line))
      ) {
        return true;
      }
    }
  }

  for (let i = 0; i < n; i++) {
    // Here we deter from the server-side implementation because `intersectWithSegment` does not return the
    // `LineSegment2D` type.
    if (segments[i].overlaps(segments[(i + 1) % n])) {
      return true;
    }
  }

  return false;
}

/**
 * This is the client-side version of `PolygonValidationUtilities.ConnectedSegmentsFormConvexPolygon`
 */
function connectedSegmentsFormConvexPolygon(segments: readonly LineSegment2D[]) {
  if (segments.length < 3) {
    return false;
  }

  let totalAngle = 0;
  for (let i = 0; i < segments.length; i++) {
    const segment1 = segments[i];
    const segment2 = segments[(i + 1) % segments.length];
    const angle = segment1.direction.angleBetween(segment2.direction);

    if (angleEquals(angle, straightAngle)) return false;

    totalAngle += angle;

    if (!angleLessThanOrEqual(totalAngle, fullCircle)) return false;
  }

  return true;
}

/**
 * Given a loop of vertices representing a region of the plane, calculate the polygons which are an equivalent
 * representation of the plane. Useful for converting a polyline with "wasp-like" sections into a collection of non-
 * degenerate polygons. May throw or return null if `vertices` represents a polyline that fully crosses itself.
 */
export function vertexLoopToPolygons2D(vertices: readonly Point2D[]): Polygon2D[] | null {
  const unsplitEdges = createEdgesFromVertexLoop(vertices);

  // Add in any additional edge intersections not represented by vertices
  const allIntersections = allPairs(unsplitEdges)
    .map(([edge1, edge2]) => {
      const intersection = edge1.intersectWithSegment(edge2);
      return !intersection ||
        [edge1, edge2].every((edge) => edge.endPoints.some((endpoint) => endpoint.equals(intersection)))
        ? null
        : intersection;
    })
    .filter(notNullish);

  const splitEdges = unsplitEdges.flatMap((edge) => {
    const intersections = uniqWith(
      sortBy(allIntersections.filter(edge.containsOnInside.bind(edge)), (i) => i.getCoordinate(edge.direction)),
      (p1, p2) => p1.equals(p2),
    );
    return pairwise([edge.basePoint, ...intersections, edge.endPoint]).map(([b, e]) => new LineSegment2D(b, e));
  });

  // We "key" each vertex to associate it with equivalent vertices. Unique vertices have a key of `null`.
  const vertexList = splitEdges.map((edge, index) => ({
    vertex: edge.basePoint,
    index,
    key: null as number | null,
    count: 1,
  }));
  let nextKey = 0;
  for (const tuple of vertexList) {
    const { vertex, index, key } = tuple;
    if (key !== null) continue;
    const equivalentVertices = vertexList.slice(index).filter((tuple) => tuple.vertex.equals(vertex));
    const count = equivalentVertices.length;
    if (count > 1) {
      for (const equivalentVertex of equivalentVertices) {
        equivalentVertex.key = nextKey;
        equivalentVertex.count = count;
      }
      nextKey += 1;
    }
  }

  const polygons = [];
  // Now, find runs of vertices that start and end with same-key vertices, with no inner repeats. Each corresponds
  // to a single polygon or a degenerate region. Remove those vertices as a polygon and repeat until we are done.
  while (true) {
    // Base case: no repeats. This should be a single polygon or degenerate region.
    if (vertexList.every(({ key }) => key === null)) {
      const points = vertexList.map(({ vertex }) => vertex);
      const polygon = Polygon2D.fromVerticesIfValid(points);
      if (polygon) polygons.push(polygon);
      return polygons;
    }

    const nextSection =
      pairwise(vertexList.map((vertex, index) => ({ vertex, index })).filter(({ vertex: { key } }) => key !== null))
        .map(([v1, v2]) => (v1.vertex.key === v2.vertex.key ? { v1, v2 } : null))
        .filter(notNullish)
        .find(() => true) ?? null;

    // Degnerate input, such as a loop-de-loop, is addressed here.
    if (!nextSection) return null;

    // Trim out the slice described by nextSection
    const start = nextSection.v1.index;
    const end = nextSection.v2.index;
    const count = end - start;
    const loop = vertexList.slice(start, end).map(({ vertex }) => vertex);
    const polygon = Polygon2D.fromVerticesIfValid(loop);
    if (polygon) polygons.push(polygon);
    vertexList.splice(start, count);
    const keyInQuestion = nextSection.v1.vertex.key;
    for (const v of vertexList) {
      if (v.key === keyInQuestion) {
        v.count -= 1;
        if (v.count === 1) v.key = null;
      }
    }
  }
}

function createEdgesFromVertexLoop(vertices: readonly Point2D[]): LineSegment2D[] {
  if (vertices.length === 0) return [];

  const result: LineSegment2D[] = [];

  // First pass to construct the initial edges
  let prevVertex = vertices[0];
  for (const vertex of vertices.slice(1)) {
    if (prevVertex.equals(vertex)) continue;
    result.push(new LineSegment2D(prevVertex, vertex));
    prevVertex = vertex;
  }

  // Second pass to remove edges from the end that can't be joined to the first vertex
  while (result.length > 0 && result[result.length - 1].endPoint.equals(result[0].basePoint)) {
    result.pop();
  }

  // Join the end to the beginning
  if (result.length > 0) {
    result.push(new LineSegment2D(result[result.length - 1].endPoint, result[0].basePoint));
  }

  return result;
}

/**
 * Given a loop of vertices representing a region of the plane, calculate the polygons which are an equivalent
 * representation of the plane. Useful for converting a polyline with "wasp-like" sections into a collection of non-
 * degenerate polygons. May throw or return null if `vertices` represents a polyline that fully crosses itself, or that
 * cannot be contained in a plane.
 */
export function vertexLoopToPolygons(vertices: readonly Point3D[]): Polygon3D[] | null {
  const normalDirection = tryGetNormalDirection(vertices);
  if (!normalDirection) return null;
  const embedding = getEmbedding(vertices, normalDirection);
  return (
    vertexLoopToPolygons2D(vertices.map(embedding.pointTo2D.bind(embedding)))?.map(
      embedding.polygonTo3D.bind(embedding),
    ) ?? null
  );
}
